import { api } from '@/lib/api';
import type { SourceCoverage } from '@waypoint/shared';

export interface EscalationAction {
  id: string;
  ruleName: string;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium';
  channel: string;
  status: 'pending' | 'approved' | 'dismissed';
  subject: string;
  body: string;
  suggestedMessage: string | null;
  targetEntity: string | null;
  triggerValue: string;
  thresholdValue: string;
  deadline: string | null;
  createdAt: string;
  /** signal rows: chance you reply within 48h (email_response_priority). Null unless a trained model serves it. */
  replyLikelihood?: number | null;
  /** task rows: chance it is done by its due date (task_completion). Null unless a trained model serves it. */
  onTimeProbability?: number | null;
  /** signal rows backed by an inbound thread: who sent it. Null means nothing to reply to or mute. */
  sender?: string | null;
}

export interface EscalationResult {
  actions: EscalationAction[];
  /** null unless email and billing are connected and synced: "0 pending" is a claim. */
  pendingCount?: number | null;
  coverage?: SourceCoverage;
}

export async function getEscalationActions(): Promise<EscalationResult> {
  return api('/v1/escalations');
}

/**
 * What an escalation id points at. The feed is three sources behind one shape
 * (routes/escalations.ts): `signal-<uuid>` an email signal, `action-<uuid>` a
 * failed action, `task-<uuid>` a high-priority task. Dismiss / Retry route on it.
 */
export type EscalationTarget = { kind: 'signal' | 'action' | 'task'; id: string };

export function escalationTarget(id: string): EscalationTarget | null {
  const m = /^(signal|action|task)-(.+)$/.exec(id);
  return m ? { kind: m[1] as EscalationTarget['kind'], id: m[2]! } : null;
}
